import type { ListApprovalParams, ListRequestParams } from '../api/request-center.api'

/**
 * Typed cache keys for the Request Center feature.
 *
 * Per ADR-0012: query keys are typed constants, never inline string arrays.
 * Each accessor reproduces the literal prefix the inline `useQuery` /
 * `invalidateQueries` call sites previously used, so existing
 * `invalidateQueries({ queryKey: ['approvals-inbox'] })` prefix matches keep
 * working without any caller-side rewrite. Two `request-types` shapes coexist
 * (camelCase `requestTypes` vs kebab `request-types`) because the pages were
 * authored with different conventions and merging them would change cache
 * behavior — preserved verbatim as a behavior-preserving migration.
 */
export const requestCenterKeys = {
	requests: {
		all: ['requests'] as const,
		list: (params: { page: number; filters: ListRequestParams }) => ['requests', params] as const,
		detail: (id: string) => ['request', id] as const,
		adminList: (params: unknown) => ['admin-requests', params] as const,
	},
	types: {
		all: ['request-types'] as const,
		catalog: () => ['request-types', { catalog: true }] as const,
		listWithInactiveKebab: () => ['request-types', { includeInactive: true }] as const,
		listWithInactiveCamel: () => ['requestTypes', { includeInactive: true }] as const,
		bySlug: (slug: string) => ['request-type', slug] as const,
		forFilter: () => ['request-types-for-filter'] as const,
		admin: () => ['request-types', { admin: true }] as const,
	},
	approvals: {
		all: ['approvals-inbox'] as const,
		inbox: (params: ListApprovalParams) => ['approvals-inbox', params] as const,
		detail: (id: string) => ['approval', id] as const,
		timelineByRequest: (requestId: string) => ['approvals-timeline', requestId] as const,
	},
	audit: {
		byRequest: (requestId: string) => ['request-audit', requestId] as const,
		stateHistory: (requestId: string) => ['request-state-history', requestId] as const,
		adminSearch: (filters: unknown, page: number) => ['admin-audit-search', filters, page] as const,
	},
	comments: {
		byRequest: (requestId: string) => ['comments', 'request_center_request', requestId] as const,
	},
	tickets: {
		byRequest: (requestId: string) => ['tickets-by-request', requestId] as const,
	},
	dataSources: {
		all: ['data-sources'] as const,
		searchAll: (key: string) => ['data-source-search', key, { all: true }] as const,
	},
	systems: {
		all: ['systems'] as const,
	},
	stats: {
		my: () => ['stats', 'my'] as const,
	},
	userDisplay: {
		byId: (userId: string) => ['user-display', userId] as const,
	},
}
